import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import {
  FoodSafetyExceptionFollowUp,
  RecheckDue,
} from 'src/components/food-safety/FoodSafetyExceptionFollowUp'
import { FOOD_SAFETY_SECONDARY_TEXT } from 'src/constants/foodSafety'
import useFoodSafetyExceptionFollowUps from 'src/hooks/useFoodSafetyExceptionFollowUps'
import useFoodSafetyLatestRequest from 'src/hooks/useFoodSafetyLatestRequest'
import { fetchFoodSafetyExceptions } from 'src/utils/foodSafetyApi'
import {
  formatFoodSafetyExceptionHeadline,
  formatFoodSafetyExceptionWhen,
  formatFoodSafetyRecheckDue,
  formatFoodSafetyUnresolvedTitle,
  hasFoodSafetyRecheckDue,
  isFoodSafetyRecheckOverdue,
  jumpToFoodSafetyException,
} from 'src/utils/foodSafetyCorrectiveAction'

const UNRESOLVED_LIMIT = 20
/** How often "Recheck due" re-checks the clock so it can turn overdue. */
const RECHECK_DUE_TICK_MS = 30 * 1000

const exceptionName = exception =>
  [exception?.definitionName, exception?.context?.productName]
    .filter(Boolean)
    .join(' · ')
/** Express caps the exceptions list here. */
const UNRESOLVED_SHOW_ALL_LIMIT = 200

/**
 * "Needs action" strip at the top of a section: open exceptions for this
 * section. The viewed day's carry their follow-up on their own row, so here
 * they get "Go to it" (jumps to the row); other days get the follow-up
 * inline. `includeViewedDay` for screens whose rows don't carry it.
 * `autoJump` (Home chip): once loaded, go to the first open exception.
 * Hidden when there are none or the load fails (never blocks logging).
 */
const FoodSafetyUnresolvedStrip = ({
  venueId,
  section,
  viewedLocalDate,
  todayLocalDate,
  refreshKey,
  includeViewedDay = false,
  autoJump = false,
  onResolved,
  testIdPrefix = 'fs-unresolved',
}) => {
  const [state, setState] = useState({
    exceptions: [],
    openCount: 0,
    recheckDue: [],
  })
  const [showAll, setShowAll] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())

  const latest = useFoodSafetyLatestRequest()

  const load = useCallback(async () => {
    if (!venueId || !section) return
    const isCurrent = latest.begin()
    try {
      const data = await fetchFoodSafetyExceptions(venueId, {
        status: 'open',
        section,
        limit: showAll ? UNRESOLVED_SHOW_ALL_LIMIT : UNRESOLVED_LIMIT,
        includeRecheckDue: true,
      })
      if (!isCurrent()) return
      setState({
        exceptions: data.exceptions,
        openCount: data.openCount,
        recheckDue: data.recheckDue || [],
      })
      setNowMs(Date.now())
    } catch {
      if (!isCurrent()) return
      setState({ exceptions: [], openCount: 0, recheckDue: [] })
    } finally {
      isCurrent.done()
    }
  }, [venueId, section, latest, showAll])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const hasRecheckDue = state.recheckDue.length > 0
  useEffect(() => {
    if (!hasRecheckDue) return undefined
    const timer = setInterval(() => setNowMs(Date.now()), RECHECK_DUE_TICK_MS)
    return () => clearInterval(timer)
  }, [hasRecheckDue])

  const followUps = useFoodSafetyExceptionFollowUps({
    venueId,
    onSaved: async () => {
      await load()
      if (onResolved) await onResolved()
    },
  })

  const listed = state.exceptions.filter(exception => exception?._id)
  const hereItems = includeViewedDay
    ? []
    : listed.filter(exception => exception.localDate === viewedLocalDate)
  const items = includeViewedDay
    ? listed
    : listed.filter(exception => exception.localDate !== viewedLocalDate)
  const itemIds = new Set(listed.map(exception => exception._id))
  const dueItems = state.recheckDue.filter(
    exception =>
      exception?._id &&
      !itemIds.has(exception._id) &&
      hasFoodSafetyRecheckDue(exception),
  )
  const more = Math.max(0, state.openCount - state.exceptions.length)
  const total = hereItems.length + items.length + more
  const viewingToday = viewedLocalDate === todayLocalDate

  const openFollowUp = followUps.open
  const goTo = exception => {
    if (!jumpToFoodSafetyException(exception._id)) followUps.open(exception)
  }

  const firstHere = hereItems[0] || null
  const firstOther = items[0] || null
  const jumped = useRef(false)
  useEffect(() => {
    if (!autoJump || jumped.current || (!firstHere && !firstOther)) return
    jumped.current = true
    const run = () => {
      if (firstHere && jumpToFoodSafetyException(firstHere._id)) return
      const link = document.querySelector(
        `[data-testid="${testIdPrefix}-record-action-${firstOther?._id}"]`,
      )
      if (link) {
        link.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
        link.focus?.()
      } else if (firstHere) {
        openFollowUp(firstHere)
      }
    }
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run)
    else run()
  }, [autoJump, firstHere, firstOther, testIdPrefix, openFollowUp])

  if (total === 0 && dueItems.length === 0) return null

  return (
    <Strip
      role='region'
      aria-label='Unresolved items'
      data-testid={testIdPrefix}
    >
      {dueItems.length > 0 ? (
        <>
          <Title data-testid={`${testIdPrefix}-recheck-due-title`}>
            {dueItems.length === 1 ? 'Recheck due' : 'Rechecks due'}
          </Title>
          <List>
            {dueItems.map(exception => (
              <Item
                key={exception._id}
                data-testid={`${testIdPrefix}-recheck-due-${exception._id}`}
              >
                <ItemHead>
                  <ItemName>{exceptionName(exception) || 'Reading'}</ItemName>
                  <RecheckDue
                    data-overdue={
                      isFoodSafetyRecheckOverdue(exception, nowMs)
                        ? 'true'
                        : 'false'
                    }
                    data-testid={`${testIdPrefix}-recheck-due-time-${exception._id}`}
                  >
                    {formatFoodSafetyRecheckDue(exception, nowMs)}
                  </RecheckDue>
                </ItemHead>
                <ItemHeadline>
                  {formatFoodSafetyExceptionHeadline(exception)}
                </ItemHeadline>
                <FoodSafetyExceptionFollowUp
                  exception={exception}
                  section={section}
                  viewingToday
                  followUps={followUps}
                  testIdPrefix={testIdPrefix}
                />
              </Item>
            ))}
          </List>
        </>
      ) : null}
      {total > 0 ? (
        <Title data-testid={`${testIdPrefix}-title`}>
          {formatFoodSafetyUnresolvedTitle(total)}
        </Title>
      ) : null}
      {hereItems.length > 0 ? (
        <>
          <Sub>
            {viewingToday ? 'Today' : 'This day'} — on the readings below.
          </Sub>
          <List>
            {hereItems.map(exception => (
              <Item
                key={exception._id}
                data-testid={`${testIdPrefix}-here-${exception._id}`}
              >
                <ItemHead>
                  <ItemName>{exceptionName(exception) || 'Reading'}</ItemName>
                  <ItemWhen>
                    {formatFoodSafetyExceptionWhen(exception, {
                      todayLocalDate,
                    })}
                  </ItemWhen>
                </ItemHead>
                <ItemHeadline>
                  {formatFoodSafetyExceptionHeadline(exception)}
                </ItemHeadline>
                {followUps.drafts[exception._id] ? (
                  <FoodSafetyExceptionFollowUp
                    exception={exception}
                    section={section}
                    viewingToday={exception.localDate === todayLocalDate}
                    followUps={followUps}
                    testIdPrefix={testIdPrefix}
                  />
                ) : (
                  <ShowAll
                    type='button'
                    onClick={() => goTo(exception)}
                    data-testid={`${testIdPrefix}-go-${exception._id}`}
                  >
                    Go to it
                  </ShowAll>
                )}
              </Item>
            ))}
          </List>
        </>
      ) : null}
      {items.length > 0 ? (
        <>
          <Sub>
            {includeViewedDay
              ? 'Record corrective action.'
              : 'From other days — record corrective action.'}
          </Sub>
          <List>
            {items.map(exception => {
              const name = exceptionName(exception)
              return (
                <Item
                  key={exception._id}
                  data-testid={`${testIdPrefix}-item-${exception._id}`}
                >
                  <ItemHead>
                    <ItemName>{name || 'Reading'}</ItemName>
                    <ItemWhen>
                      {formatFoodSafetyExceptionWhen(exception, {
                        todayLocalDate,
                      })}
                    </ItemWhen>
                  </ItemHead>
                  <ItemHeadline>
                    {formatFoodSafetyExceptionHeadline(exception)}
                  </ItemHeadline>
                  <FoodSafetyExceptionFollowUp
                    exception={exception}
                    section={section}
                    viewingToday={exception.localDate === todayLocalDate}
                    followUps={followUps}
                    testIdPrefix={testIdPrefix}
                  />
                </Item>
              )
            })}
          </List>
          {more > 0 && !showAll ? (
            <ShowAll
              type='button'
              onClick={() => setShowAll(true)}
              data-testid={`${testIdPrefix}-show-all`}
            >
              Show all ({total})
            </ShowAll>
          ) : null}
          {more > 0 && showAll ? (
            <Sub data-testid={`${testIdPrefix}-capped`}>
              Showing the first {items.length} — record these to see the rest.
            </Sub>
          ) : null}
        </>
      ) : null}
    </Strip>
  )
}

const Strip = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: 0.75rem 1rem;
  border: 1px solid #f0d9b5;
  border-radius: 8px;
  background: #fffaf2;
`

const Title = styled.p`
  margin: 0;
  font-weight: 700;
  color: #8a4b00;
`

const Sub = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ShowAll = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  text-decoration: underline;
`

const List = styled.ul`
  list-style: none;
  margin: 0.25rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-top: 0.6rem;
  border-top: 1px solid #f0d9b5;
`

const ItemHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`

const ItemName = styled.span`
  font-weight: 600;
`

const ItemWhen = styled.span`
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ItemHeadline = styled.span`
  font-size: 0.9rem;
  color: #a35b00;
`

export default FoodSafetyUnresolvedStrip
